import * as React from 'react'

import { Site } from '@envato/foundation-types'

import { config } from '../../../../common/config'
import { hasFeature } from '../../../../common/utils/features'
import NextGenImageWrapper from '../../nextGenImageWrapper'
import SeoHeadings from '../../seoHeadings/seoHeadings'
import { ICommonLocation } from '../../../../common/utils/location/types'
import { IExperimentsState } from '../../../../common/reducers/experiments'

import * as styles from './searchHeaderBlock.module.css'
import * as seoHeadingsStyles from '../../../components/seoHeadings/themes/homepage.newdesign.module.css'
import * as seoHeadingsSmallStyles from '../../../components/seoHeadings/themes/homepage.newdesign.small.module.css'

interface IProps {
  originalHeader?: string
  originalSubHeading?: string
  currentSite: Site
  location: ICommonLocation
  experiments: IExperimentsState
}

const siteHeaderTitleMap = new Map([
  [Site.themeforest, 'Thousands of WordPress themes and website templates'],
  [Site.codecanyon, 'Discover thousands of code scripts and plugins'],
  [Site.videohive, 'Discover millions of video effects and stock footage'],
  [
    Site.audiojungle,
    'Discover thousands of royalty free music and audio tracks'
  ],
  [Site.graphicriver, 'Discover thousands of fonts, logos & presentations'],
  [Site.photodune, 'Discover millions of royalty-free stock photos'],
  [Site.threedocean, 'Discover thousands of 3D print models and CG textures']
])

const siteHeaderTitleMapVariantWithoutDollar = new Map([
  [Site.themeforest, '{itemCount} WordPress Themes & Website Templates'],
  [Site.codecanyon, 'Discover {itemCount} code scripts and plugins'],
  [Site.videohive, 'Discover {itemCount} Video effects and stock footage'],
  [Site.audiojungle, 'Royalty free music and audio tracks'],
  [Site.graphicriver, 'Discover {itemCount} fonts, logos & presentations'],
  [Site.photodune, 'Discover royalty free stock photos'],
  [Site.threedocean, 'Discover {itemCount} 3D print models and CG textures']
])

const siteHeaderTitleMapVariantWithDollar = new Map([
  [
    Site.themeforest,
    '{itemCount} WordPress themes and website templates from $2'
  ],
  [Site.codecanyon, 'Discover {itemCount} code scripts and plugins from $2'],
  [
    Site.videohive,
    'Discover {itemCount} Video effects and stock footage from $4'
  ],
  [
    Site.audiojungle,
    'Discover {itemCount} royalty free music and audio tracks from $1'
  ],
  [
    Site.graphicriver,
    'Discover {itemCount} fonts, logos & presentations from $2'
  ],
  [Site.photodune, 'Discover royalty free stock photos from $2'],
  [
    Site.threedocean,
    'Discover {itemCount} 3D print models and CG textures from $2'
  ]
])

const SearchHeaderBlock: React.SFC<IProps> = ({
  children,
  currentSite,
  originalHeader,
  originalSubHeading,
  location,
  experiments
}) => {
  const IMG_URL_DESKTOP = `${config.assets.PUBLIC_PATH}/images/home-page/new-design/${currentSite}/search-header-1600749088571`
  const imageStyle =
    currentSite === Site.audiojungle
      ? styles.imageWrapperAudio
      : styles.imageWrapper

  const hasOgCopyVerbatim = hasFeature(
    'ogCopyVerbatim',
    currentSite,
    location,
    experiments
  )

  const hasCombinedCopyNoDollars = hasFeature(
    'combinedCopyNoDollars',
    currentSite,
    location,
    experiments
  )

  const hasCombinedCopyWithDollars = hasFeature(
    'combinedCopyWithDollars',
    currentSite,
    location,
    experiments
  )

  let header = siteHeaderTitleMap.get(currentSite)
  let subHeader
  let headerStyle = seoHeadingsStyles
  let seoHeaderClass = styles.seoHeader

  if (hasOgCopyVerbatim) {
    header = originalHeader
    subHeader = originalSubHeading
    headerStyle = seoHeadingsSmallStyles
    seoHeaderClass = styles.seoHeaderSmall
  }

  if (hasCombinedCopyNoDollars || hasCombinedCopyWithDollars) {
    const itemCount =
      originalHeader?.match(/\s?([0-9,])+ /) ||
      originalSubHeading?.match(/\s?([0-9,])+ /)

    header = (hasCombinedCopyNoDollars
      ? siteHeaderTitleMapVariantWithoutDollar
      : siteHeaderTitleMapVariantWithDollar
    ).get(currentSite)

    if (itemCount) {
      header = header?.replace('{itemCount}', itemCount[0])
    }
  }

  return (
    <section className={styles.root}>
      <div className={styles.wrapper}>
        <div className={styles.autosuggestWrapper}>
          <div className={seoHeaderClass}>
            <SeoHeadings
              TagName="h1"
              heading={
                header || 'Discover thousands of web themes and templates'
              }
              subHeading={subHeader || ''}
              styles={headerStyle}
            />
          </div>
          {children}
        </div>
        <div className={imageStyle}>
          <NextGenImageWrapper>
            <img
              src={`${IMG_URL_DESKTOP}.webp`}
              alt={`${currentSite} hero image`}
              className={styles.image}
            />
          </NextGenImageWrapper>
        </div>
      </div>
    </section>
  )
}

export default SearchHeaderBlock
